// src/Helpers/authStorage.js
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

const AUTH_STORAGE_KEY = "USER_AUTH";
const REFRESH_STORAGE_KEY = "USER_REFRESH";
const AUTH_METADATA_KEY = "USER_AUTH_META";
const PERSISTENCE_KEY = "USER_AUTH_PERSIST";

const SECURE_ACCESS_KEY = "secure.auth.access";
const SECURE_REFRESH_KEY = "secure.auth.refresh";
const SECURE_PERSIST_KEY = "secure.auth.persist";
const SECURE_METADATA_KEY = "secure.auth.meta";

const STORAGE_HANDLERS = [
  {
    source: "localStorage",
    getStorage: () => (typeof window !== "undefined" ? window.localStorage : null),
  },
  {
    source: "sessionStorage",
    getStorage: () => (typeof window !== "undefined" ? window.sessionStorage : null),
  },
];

const isNativePlatform = () => {
  try {
    return (
      typeof Capacitor !== "undefined" &&
      typeof Capacitor.isNativePlatform === "function" &&
      Capacitor.isNativePlatform()
    );
  } catch {
    return false;
  }
};

const normalizeEntry = (storage, source) => {
  if (!storage) return null;
  const raw = storage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;

  let token = null;
  let metadata = null;

  if (raw.trim().startsWith("{")) {
    try {
      metadata = JSON.parse(raw);
      token =
        metadata?.access ||
        metadata?.token ||
        metadata?.Authorization ||
        metadata?.auth ||
        metadata?.jwt ||
        metadata?.value ||
        null;
    } catch {
      storage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  } else {
    token = raw;
  }

  if (!token || typeof token !== "string" || !token.trim()) {
    storage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }

  return {
    token: token.trim(),
    metadata,
    raw,
    source,
    storage,
  };
};

const readStoredMetadata = (storage) => {
  if (!storage) return null;
  const raw = storage.getItem(AUTH_METADATA_KEY);
  if (!raw || !raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    storage.removeItem(AUTH_METADATA_KEY);
    return null;
  }
};

const writeWebToken = (key, value, persistSession) => {
  if (typeof window === "undefined") return;
  const primary = persistSession ? window.localStorage : window.sessionStorage;
  const secondary = persistSession ? window.sessionStorage : window.localStorage;
  if (value) {
    primary?.setItem(key, value);
  } else {
    primary?.removeItem(key);
  }
  secondary?.removeItem(key);
};

const setSecureValue = async (key, value) => {
  if (!isNativePlatform()) return;
  try {
    if (value) {
      await Preferences.set({ key, value });
    } else {
      await Preferences.remove({ key });
    }
  } catch (error) {
    console.warn(`[authStorage] secure set error for ${key}`, error);
  }
};

const getSecureValue = async (key) => {
  if (!isNativePlatform()) return null;
  try {
    const { value } = await Preferences.get({ key });
    return value || null;
  } catch {
    return null;
  }
};

const getWebRefreshToken = () => {
  if (typeof window === "undefined") return "";
  return (
    window.localStorage?.getItem(REFRESH_STORAGE_KEY) ||
    window.sessionStorage?.getItem(REFRESH_STORAGE_KEY) ||
    ""
  );
};

const setPersistenceFlag = (persistSession) => {
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(PERSISTENCE_KEY, persistSession ? "local" : "session");
    window.sessionStorage?.setItem(PERSISTENCE_KEY, persistSession ? "local" : "session");
  }
  if (isNativePlatform()) {
    Preferences.set({ key: SECURE_PERSIST_KEY, value: persistSession ? "local" : "session" }).catch(
      () => {}
    );
  }
};

const readPersistenceFlag = async () => {
  if (isNativePlatform()) {
    try {
      const { value } = await Preferences.get({ key: SECURE_PERSIST_KEY });
      if (value === "session") return false;
      if (value === "local") return true;
    } catch {
      return true;
    }
  }
  if (typeof window === "undefined") return true;
  const stored =
    window.localStorage?.getItem(PERSISTENCE_KEY) ||
    window.sessionStorage?.getItem(PERSISTENCE_KEY);
  if (stored === "session") return false;
  if (stored === "local") return true;
  return true;
};

export const storeAuthTokens = async ({ accessToken, refreshToken, persistSession, metadata }) => {
  const persistFlag =
    typeof persistSession === "boolean" ? persistSession : await readPersistenceFlag();
  setPersistenceFlag(persistFlag);

  writeWebToken(AUTH_STORAGE_KEY, accessToken || "", persistFlag);
  writeWebToken(REFRESH_STORAGE_KEY, refreshToken || "", persistFlag);
  const sanitizedMetadata = { ...(metadata || {}) };
  delete sanitizedMetadata.access;
  delete sanitizedMetadata.refresh;
  const metadataPayload =
    sanitizedMetadata && Object.keys(sanitizedMetadata).length
      ? JSON.stringify(sanitizedMetadata)
      : "";
  writeWebToken(AUTH_METADATA_KEY, metadataPayload, persistFlag);

  if (isNativePlatform()) {
    await setSecureValue(SECURE_ACCESS_KEY, accessToken || "");
    await setSecureValue(SECURE_REFRESH_KEY, refreshToken || "");
    await setSecureValue(SECURE_METADATA_KEY, metadataPayload || "");
  }
};

export const safeGetAuthToken = () => {
  const buildEntry = ({ getStorage, source }) => {
    const storage = getStorage();
    if (!storage) return null;
    return normalizeEntry(storage, source);
  };

  const attachMetadata = (entry) => {
    if (!entry) return null;
    const merged =
      entry.metadata && typeof entry.metadata === "object" ? { ...entry.metadata } : {};
    const storedMeta = readStoredMetadata(entry.storage);
    const finalMetadata =
      storedMeta || Object.keys(merged).length ? { ...merged, ...(storedMeta || {}) } : null;
    return { ...entry, metadata: finalMetadata };
  };

  const [rawLocalEntry, rawSessionEntry] = STORAGE_HANDLERS.map(buildEntry);
  const localEntry = attachMetadata(rawLocalEntry);
  const sessionEntry = attachMetadata(rawSessionEntry);

  const localToken = localEntry?.token;
  const sessionToken = sessionEntry?.token;

  if (sessionEntry && (!localEntry || sessionToken !== localToken)) {
    if (localEntry?.storage) {
      localEntry.storage.removeItem(AUTH_STORAGE_KEY);
    }
    return sessionEntry;
  }

  if (localEntry) {
    if (sessionEntry && sessionToken !== localToken && sessionEntry.storage) {
      sessionEntry.storage.removeItem(AUTH_STORAGE_KEY);
    }
    return localEntry;
  }

  return null;
};

export const getStoredAccessToken = async () => {
  if (isNativePlatform()) {
    const secure = await getSecureValue(SECURE_ACCESS_KEY);
    if (secure) return secure;
  }
  return safeGetAuthToken()?.token || "";
};

export const getStoredRefreshToken = async () => {
  if (isNativePlatform()) {
    const secure = await getSecureValue(SECURE_REFRESH_KEY);
    if (secure) return secure;
  }
  return getWebRefreshToken();
};

export const getAuthPersistence = async () => {
  return readPersistenceFlag();
};

export const clearAuthStorage = async () => {
  STORAGE_HANDLERS.forEach(({ getStorage }) => {
    const storage = getStorage();
  if (storage) {
      storage.removeItem(AUTH_STORAGE_KEY);
      storage.removeItem(REFRESH_STORAGE_KEY);
      storage.removeItem(AUTH_METADATA_KEY);
      storage.removeItem(PERSISTENCE_KEY);
    }
  });
  await setSecureValue(SECURE_ACCESS_KEY, "");
  await setSecureValue(SECURE_REFRESH_KEY, "");
  await setSecureValue(SECURE_PERSIST_KEY, "");
  await setSecureValue(SECURE_METADATA_KEY, "");
};

export const AUTH_STORAGE_UTILS = {
  safeGetAuthToken,
  clearAuthStorage,
  storeAuthTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  getAuthPersistence,
};
